import React from 'react';
import { cn } from '../../../../utils/cn';
import { CARD } from './dashboard-ui';

type PanelProps = {
  title: string;
  aside?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
};

export const Panel = ({ title, aside, className, children }: PanelProps) => (
  <section className={cn(CARD, 'p-5', className)}>
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="m-0 text-[16px] font-gilroy-bold text-neutral-500">{title}</h2>
      {aside && <span className="text-[13px] tabular-nums text-neutral-300">{aside}</span>}
    </div>
    {children}
  </section>
);

export const BarRows = ({ rows }: { rows: { label: string; value: number; shown?: string }[] }) => {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="m-0 list-none p-0">
      {rows.map((row) => (
        <li key={row.label} className="flex items-center gap-3 py-[7px]">
          <span className="w-[136px] shrink-0 truncate text-[13px] text-neutral-500">
            {row.label}
          </span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-creams-300">
            <div
              className="h-full rounded-full bg-primary-500"
              style={{ width: `${(row.value / max) * 100}%` }}
            />
          </div>
          <span className="min-w-[28px] text-right text-[13px] font-gilroy-bold tabular-nums text-neutral-500">
            {row.shown ?? row.value.toLocaleString()}
          </span>
        </li>
      ))}
    </ul>
  );
};

export const Skeleton = ({ className }: { className?: string }) => (
  <span
    className={cn('block animate-pulse rounded bg-gray-200 motion-reduce:animate-none', className)}
  />
);

const range = (n: number) => Array.from({ length: n }, (_, i) => i);

export const BarRowsSkeleton = ({ rows = 4 }: { rows?: number }) => (
  <ul className="m-0 list-none p-0" aria-hidden="true">
    {range(rows).map((i) => (
      <li key={i} className="flex items-center gap-3 py-[9px]">
        <Skeleton className="h-3 w-[136px] shrink-0" />
        <Skeleton className="h-2 flex-1 rounded-full" />
        <Skeleton className="h-3 w-7" />
      </li>
    ))}
  </ul>
);

export const ListSkeleton = ({ rows = 4, dot }: { rows?: number; dot?: boolean }) => (
  <ul className="m-0 list-none p-0" aria-hidden="true">
    {range(rows).map((i) => (
      <li
        key={i}
        className="flex items-center gap-3 border-t border-gray-200 py-3 first:border-t-0"
      >
        {dot && <Skeleton className="h-[9px] w-[9px] shrink-0 rounded-full" />}
        <div className="min-w-0 flex-1">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="mt-2 h-4 w-3/4" />
        </div>
      </li>
    ))}
  </ul>
);

export const TableSkeleton = ({ rows = 4, cols = 4 }: { rows?: number; cols?: number }) => (
  <div aria-hidden="true">
    {range(rows).map((r) => (
      <div
        key={r}
        className="flex items-center gap-6 border-t border-gray-200 py-3 first:border-t-0"
      >
        {range(cols).map((c) => (
          <Skeleton key={c} className={cn('h-4', c === 0 ? 'flex-1' : 'w-12')} />
        ))}
      </div>
    ))}
  </div>
);

export const PanelSkeleton = ListSkeleton;
